import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CreateFrameworkRequest, Framework, FrameworkScope, VerificationType } from '@mvcp/contracts';
import { createFramework, listFrameworks, updateFramework } from '../api';
import { FormModal } from './FormModal';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Option tuples are inlined here (with `@mvcp/contracts` as source of truth)
 * because the web consumes the contracts package as TYPES ONLY — importing the
 * runtime `FRAMEWORK_SCOPES` / `VERIFICATION_TYPES` arrays breaks `vite build`
 * (CJS __exportStar cannot be resolved by rollup; see SitesView). Exported so
 * `enum-sync.test.ts` asserts they stay byte-equal to the contract arrays.
 */
export const SCOPE_OPTIONS = ['company', 'device'] as const;
export const VERIFICATION_TYPE_OPTIONS = ['third_party', 'self_declared'] as const;

const SCOPE_LABEL: Record<FrameworkScope, string> = {
  company: 'Company',
  device: 'Device',
};
const VERIFICATION_LABEL: Record<VerificationType, string> = {
  third_party: 'Third-party',
  self_declared: 'Self-declared',
};

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; frameworks: Framework[] }
  | { kind: 'error'; message: string };

type FormState = {
  code: string;
  name: string;
  title: string;
  scope: FrameworkScope;
  verificationType: VerificationType;
  renewalMonths: string;
  effectiveDate: string;
  includeInOverall: boolean;
  includeInHealthRollup: boolean;
  maritimeOnly: boolean;
};

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  title: '',
  scope: 'company',
  verificationType: 'third_party',
  renewalMonths: '',
  effectiveDate: '',
  includeInOverall: true,
  includeInHealthRollup: true,
  maritimeOnly: false,
};

/**
 * Admin Console → Certification Catalog (Story 3.1, FR-14). Lists the seeded
 * Frameworks (mono for code/numeric/date columns per UX), an add form, and edit
 * of an existing Framework (code is immutable). Admin-only surface — belt with
 * the API's `@Roles('system_administrator')` guard and the RLS write policies.
 */
export function FrameworksView(): React.JSX.Element {
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Whether the ADD dialog is open. Editing opens the same dialog via `editingId`. */
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const frameworks = await listFrameworks();
      setState({ kind: 'ready', frameworks });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function startEdit(fw: Framework): void {
    setEditingId(fw.id);
    setFormError(null);
    setForm({
      code: fw.code,
      name: fw.name,
      title: fw.title,
      scope: fw.scope,
      verificationType: fw.verificationType,
      renewalMonths: String(fw.renewalMonths),
      effectiveDate: fw.effectiveDate,
      includeInOverall: fw.includeInOverall,
      includeInHealthRollup: fw.includeInHealthRollup,
      maritimeOnly: fw.maritimeOnly,
    });
  }

  function cancelEdit(): void {
    setEditingId(null);
    setFormError(null);
    setForm(EMPTY_FORM);
    // Also leaves the ADD disposition, so the one success path (`cancelEdit()` at the end
    // of `onSubmit`) closes the dialog whichever disposition opened it.
    setAdding(false);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);

    const renewalMonths = Number(form.renewalMonths);
    // Upper bound mirrors the API: values above the Postgres `integer` max would
    // overflow the column server-side (a 500) instead of a clean validation error.
    if (!Number.isInteger(renewalMonths) || renewalMonths <= 0 || renewalMonths > 2147483647) {
      setFormError('Renewal months must be a positive whole number (max 2147483647).');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveDate)) {
      setFormError('Effective date must be a YYYY-MM-DD date.');
      return;
    }
    if (!editingId && !form.code.trim()) {
      setFormError('Code is required.');
      return;
    }
    if (!form.name.trim() || !form.title.trim()) {
      setFormError('Name and title are required.');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        // `code` is immutable — omit it from the patch.
        await updateFramework(editingId, {
          name: form.name.trim(),
          title: form.title.trim(),
          scope: form.scope,
          verificationType: form.verificationType,
          renewalMonths,
          effectiveDate: form.effectiveDate,
          includeInOverall: form.includeInOverall,
          includeInHealthRollup: form.includeInHealthRollup,
          maritimeOnly: form.maritimeOnly,
        });
      } else {
        const input: CreateFrameworkRequest = {
          code: form.code.trim(),
          name: form.name.trim(),
          title: form.title.trim(),
          scope: form.scope,
          verificationType: form.verificationType,
          renewalMonths,
          effectiveDate: form.effectiveDate,
          includeInOverall: form.includeInOverall,
          includeInHealthRollup: form.includeInHealthRollup,
          maritimeOnly: form.maritimeOnly,
        };
        await createFramework(input);
      }
      cancelEdit();
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to save Framework');
    } finally {
      setSubmitting(false);
    }
  }

  const isEditing = editingId !== null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Certification Catalog
        </h1>
        <button
          type="button"
          className="btn-accent"
          data-surface="accent"
          onClick={() => {
            cancelEdit();
            setAdding(true);
          }}
        >
          Add a Framework
        </button>
      </div>

      {/* ONE dialog for both dispositions: `startEdit` prefills the same form, so add and
          edit cannot drift into two forms that validate differently. */}
      {(adding || isEditing) && (
      <FormModal
        title={isEditing ? 'Edit Framework' : 'Add a Framework'}
        titleId="framework-form-heading"
        onClose={() => {
          cancelEdit();
          setAdding(false);
        }}
        wide
      >
        <form
          noValidate
          onSubmit={onSubmit}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}
        >
          <label className="stack" style={{ gap: 4, flex: '0 1 130px' }}>
            <span className="field-label">Code</span>
            <input
              aria-label="Framework code"
              value={form.code}
              disabled={isEditing}
              onChange={(e) => set('code', e.target.value)}
              placeholder="e.g. NIS2"
              style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }}
            />
          </label>
          <label className="stack" style={{ gap: 4, flex: '0 1 160px' }}>
            <span className="field-label">Name</span>
            <input
              aria-label="Framework name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              style={fieldStyle}
            />
          </label>
          <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
            <span className="field-label">Title</span>
            <input
              aria-label="Framework title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              style={fieldStyle}
            />
          </label>
          <label className="stack" style={{ gap: 4, flex: '0 1 130px' }}>
            <span className="field-label">Scope</span>
            <select
              aria-label="Scope"
              value={form.scope}
              onChange={(e) => set('scope', e.target.value as FrameworkScope)}
              style={fieldStyle}
            >
              {SCOPE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {SCOPE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="stack" style={{ gap: 4, flex: '0 1 150px' }}>
            <span className="field-label">Verification</span>
            <select
              aria-label="Verification type"
              value={form.verificationType}
              onChange={(e) => set('verificationType', e.target.value as VerificationType)}
              style={fieldStyle}
            >
              {VERIFICATION_TYPE_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {VERIFICATION_LABEL[v]}
                </option>
              ))}
            </select>
          </label>
          <label className="stack" style={{ gap: 4, flex: '0 1 120px' }}>
            <span className="field-label">Renewal (mo)</span>
            <input
              aria-label="Renewal months"
              inputMode="numeric"
              value={form.renewalMonths}
              onChange={(e) => set('renewalMonths', e.target.value)}
              style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }}
            />
          </label>
          <label className="stack" style={{ gap: 4, flex: '0 1 150px' }}>
            <span className="field-label">Effective date</span>
            <input
              aria-label="Effective date"
              type="date"
              value={form.effectiveDate}
              onChange={(e) => set('effectiveDate', e.target.value)}
              style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              aria-label="Include in Overall Score"
              checked={form.includeInOverall}
              onChange={(e) => set('includeInOverall', e.target.checked)}
            />
            In Overall
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              aria-label="Include in health rollup"
              checked={form.includeInHealthRollup}
              onChange={(e) => set('includeInHealthRollup', e.target.checked)}
            />
            In Rollup
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              aria-label="Maritime only"
              checked={form.maritimeOnly}
              onChange={(e) => set('maritimeOnly', e.target.checked)}
            />
            Maritime only
          </label>
          <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
            {submitting ? 'Saving…' : isEditing ? 'Save Framework' : 'Add Framework'}
          </button>
          {isEditing && (
            <button type="button" className="icon-button" onClick={cancelEdit} disabled={submitting}>
              Cancel
            </button>
          )}
        </form>
        <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
          {formError && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {formError}
            </span>
          )}
        </div>
      </FormModal>
      )}

      <section className="card stack" aria-labelledby="frameworks-heading">
        <h2 id="frameworks-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Frameworks
        </h2>

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && <p className="muted" style={{ margin: 0 }}>{state.message}</p>}

        {state.kind === 'ready' && state.frameworks.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No frameworks yet — add one above.</p>
        )}

        {state.kind === 'ready' && state.frameworks.length > 0 && (
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Scope</th>
                <th>Verification</th>
                <th data-align="right">Renewal</th>
                <th>Effective</th>
                <th>Scoring</th>
                <th>Applies to</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {state.frameworks.map((f) => (
                <tr key={f.id}>
                  <td data-numeric>{f.code}</td>
                  <td>{f.name}</td>
                  <td style={{ textTransform: 'capitalize' }}>{f.scope}</td>
                  <td>{VERIFICATION_LABEL[f.verificationType]}</td>
                  <td data-align="right" data-numeric>
                    {f.renewalMonths}
                  </td>
                  <td data-numeric>{f.effectiveDate}</td>
                  <td>
                    {/* Both scoring flags are independently editable above, so both
                        must be visible here — otherwise a health-rollup change is
                        invisible on reload (AC3). GDPR shows Excluded / Out. */}
                    <span className="cell-cluster">
                      <span
                        className="status-pill"
                        data-tone={f.includeInOverall ? 'info' : 'neutral'}
                        title="Counts toward the Overall Score"
                      >
                        <span className="status-pill__dot" aria-hidden="true" />
                        {f.includeInOverall ? 'Overall: Scored' : 'Overall: Excluded'}
                      </span>
                      <span
                        className="status-pill"
                        data-tone={f.includeInHealthRollup ? 'info' : 'neutral'}
                        title="Counts toward the health rollup"
                      >
                        <span className="status-pill__dot" aria-hidden="true" />
                        {f.includeInHealthRollup ? 'Rollup: In' : 'Rollup: Out'}
                      </span>
                    </span>
                  </td>
                  <td>
                    {/* Data-driven applicability (Story 3.2, FR-15): a maritimeOnly
                        Framework is shown/scored only for maritime Companies. */}
                    <span
                      className="status-pill"
                      data-tone={f.maritimeOnly ? 'info' : 'neutral'}
                      title={
                        f.maritimeOnly
                          ? 'Applies only to maritime Companies'
                          : 'Applies to all Companies'
                      }
                    >
                      <span className="status-pill__dot" aria-hidden="true" />
                      {f.maritimeOnly ? 'Maritime only' : 'All companies'}
                    </span>
                  </td>
                  <td data-align="right">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => startEdit(f)}
                      aria-label={`Edit ${f.code}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </>
  );
}
